import React from "react";
import { Award, Star, CheckCircle2, Shield, Users } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";

const achievements = [
  {
    icon: Shield,
    text: "Awarded by Petroleum Development Oman (PDO) for achieving Lost-Time Injury-Free (LTI) operations — a significant safety milestone.",
  },
  {
    icon: CheckCircle2,
    text: "Recognized by Occidental Oman for maintaining LTI and RTA-free operations during casing and tubing transportation.",
  },
  {
    icon: Star,
    text: "ISO 9001:2015 Quality Management, ISO 14001:2015 Environmental Management, and ISO 45001:2018 Occupational Health & Safety certifications, accredited under IAF standards.",
  },
  {
    icon: Award,
    text: "Appreciation award from the American Embassy for outstanding logistics support provided to the United States Military during their Middle East operations.",
  },
  {
    icon: Award,
    text: "Recognized by clients including Schlumberger, Weatherford, Galfar Engineering, Larsen & Toubro Oman, and Bahwan Excel for dependable logistics and transport excellence.",
  },
  {
    icon: Users,
    text: "Annual internal Staff Excellence Awards introduced to recognize outstanding driver performance, safety leadership, and commitment to HTD’s 'One Team, One Goal' culture.",
  },
];

const Awards: React.FC<{ content?: any }> = ({ content }) => {
  const { isArabic } = useLanguage();
  const achievementsList = content?.achievements || [
    { iconName: "Shield", text: isArabic ? "حصلنا على جائزة من شركة تنمية نفط عمان (PDO) لتحقيق عمليات خالية من إصابات وقت العمل (LTI) — وهو إنجاز هام في مجال السلامة." : "Awarded by Petroleum Development Oman (PDO) for achieving Lost-Time Injury-Free (LTI) operations — a significant safety milestone." },
    { iconName: "CheckCircle2", text: isArabic ? "تكريم من شركة أوكسيدنتال عمان للحفاظ على عمليات خالية من الإصابات والحوادث أثناء نقل الأنابيب والغلاف." : "Recognized by Occidental Oman for maintaining LTI and RTA-free operations during casing and tubing transportation." },
    { iconName: "Star", text: isArabic ? "شهادات ISO 9001:2015 و ISO 14001:2015 و ISO 45001:2018 المعتمدة من الهيئة الدولية للاعتماد (IAF)." : "ISO 9001:2015 Quality Management, ISO 14001:2015 Environmental Management, and ISO 45001:2018 Occupational Health & Safety certifications, accredited under IAF standards." },
    { iconName: "Award", text: isArabic ? "شهادة تقدير من السفارة الأمريكية للدعم اللوجستي المتميز المقدم للقوات المسلحة الأمريكية أثناء عملياتها بالشرق الأوسط." : "Appreciation award from the American Embassy for outstanding logistics support provided to the United States Military during their Middle East operations." },
    { iconName: "Award", text: isArabic ? "تكريم من عملاء بارزين مثل شلمبرجير وويذرفورد وجلفار للمقاولات ولارسن وتوبرو عمان لتميز الخدمات اللوجستية." : "Recognized by clients including Schlumberger, Weatherford, Galfar Engineering, Larsen & Toubro Oman, and Bahwan Excel for dependable logistics and transport excellence." },
    { iconName: "Users", text: isArabic ? "جوائز التميز السنوية للموظفين لتكريم السائقين وتجسيد شعار 'فريق واحد، هدف واحد'." : "Annual internal Staff Excellence Awards introduced to recognize outstanding driver performance, safety leadership, and commitment to HTD’s 'One Team, One Goal' culture." },
  ];

  const customAwardImages = [];
  for (let i = 1; i <= 5; i++) {
    const img = content?.[`award_image_${i}`];
    if (img) customAwardImages.push(img);
  }

  const awardImages = customAwardImages.length > 0 ? customAwardImages : (content?.images || [
    "/award/awards_1.jpg",
    "/award/awards_2.jpg",
    "/award/awards_3.jpg",
    "/award/awards_4.jpg",
    "/award/awards_5.jpg",
  ]);

  const iconMap: Record<string, any> = { Award, Star, CheckCircle2, Shield, Users };

  return (
    <section className="py-10 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            {content?.title || "Awards & Recognitions"}
          </h2>
          <p className="mt-6 text-gray-500 max-w-5xl mx-auto text-[15px] md:text-[16px] leading-relaxed text-justify md:text-center">
            {content?.desc || "Celebrating our milestones of excellence..."}
          </p>
        </motion.div>

        {/* Achievements Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {achievementsList.map((item: any, index: number) => {
            const Icon = iconMap[item.iconName] || Award;
            return (
              <motion.div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition p-6 text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="flex items-center justify-center mb-4">
                  <div className="p-4 bg-red-100 rounded-full">
                    <Icon className="w-5 h-5 text-red-600" />
                  </div>
                </div>
                <p className="text-gray-800 text-sm text-justify leading-relaxed">
                  {item.text}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Continuous Awards Images Marquee */}
        <div className="overflow-hidden w-full relative mt-20 pt-10 border-t border-gray-100 px-4">
          <motion.div
            className="flex gap-8 items-center"
            animate={{ x: ["0%", "-50%"] }}
            transition={{
              repeat: Infinity,
              duration: 30,
              ease: "linear",
            }}
          >
            {[...awardImages, ...awardImages].map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`Award ${i + 1}`}
                className="h-16 md:h-20 lg:h-24 object-contain flex-shrink-0"
              />
            ))}
          </motion.div>

          {/* Accreditation Footer */}
          <div className="text-center mt-6 text-xs text-gray-500">
            {content?.footer || "ISO certifications accredited under IAF (International Accreditation Forum)"}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Awards;
